import { overviewIcon, toClassicIcon } from "common/defaultIcons";
import useHelpers from "composables/useHelpers.js";
import { overviewPortfolioFragment } from "portfolio/routes/fragments";
import { storeToRefs } from "pinia";
import { OVERVIEW_ROLES } from "shared/constants";
import { useModuleStore } from "store/modules/moduleStore.js";
import {
  getRouter,
  isRouteAccessibleByName,
  pickFirstAccessibleChild,
} from "router/routeAccess";

const { mappedTerms } = useHelpers();

// The module the school leans on decides which overview to land on, but a role
// need not have access to that module's overview -- an "Organisator portfolio"
// (decaan) only reaches the portfolio tab. Fall back to the first tab the role
// may actually open instead of bouncing them to Unauthorized.
const resolveOverviewRedirect = () => {
  const router = getRouter();

  if (!router) {
    return { name: "Unauthorized" };
  }

  const moduleStore = useModuleStore();
  const { redirectToCorrectModule } = storeToRefs(moduleStore);
  const overviewRoute = redirectToCorrectModule.value?.overviewRoute;

  if (overviewRoute && isRouteAccessibleByName(router, overviewRoute)) {
    return { name: overviewRoute };
  }

  return (
    pickFirstAccessibleChild(router, "overview") ?? { name: "Unauthorized" }
  );
};

const routes = [
  {
    path: "/overview",
    name: "overview",
    redirect: resolveOverviewRedirect,
    component: () => import("views/EmptyView.vue"),
    meta: {
      title: "Overzichten",
      roleRequired: OVERVIEW_ROLES,
      settingRequired: ["studentMeeting", "learningObjectives", "portfolio"],
      icon: toClassicIcon(overviewIcon),
      isMenuItem: true,
      menuSection: "main",
      menuOrder: 13,
    },
    children: [
      {
        path: "meetings",
        name: "overviewMeetings",
        component: () =>
          import("pages/overview/OverviewMeetingsOldFilters.vue"),
        meta: {
          title: `${mappedTerms.student[0].toUpperCase()}${mappedTerms.student.slice(
            1,
          )}bespreking`,
          isMenuItem: true,
          menuSection: "main",
          roleRequired: ["webmaster", "manager", "organizer", "teamleader"],
          settingRequired: ["studentMeeting"],
        },
      },
      {
        path: "transition-meetings",
        name: "overviewTransitionMeetings",
        component: () =>
          import("pages/overview/OverviewTransitionMeetings.vue"),
        meta: {
          title: "Adviesbespreking",
          isMenuItem: true,
          menuSection: "main",
          settingRequired: ["groupMeeting", "studentMeeting"],
          roleRequired: ["webmaster", "manager", "organizer", "teamleader"],
        },
      },
      {
        path: "group-meetings",
        name: "overviewGroupMeetings",
        component: () => import("pages/overview/OverviewGroupMeetings.vue"),
        meta: {
          title: "Groepsbespreking",
          isMenuItem: true,
          menuSection: "main",
          settingRequired: ["groupMeeting", "studentMeeting"],
          roleRequired: ["webmaster", "manager", "organizer", "teamleader"],
        },
      },
      {
        path: "learning-objectives",
        name: "overviewLearningObjectives",
        component: () =>
          import("pages/overview/OverviewLearningObjectives.vue"),
        meta: {
          title: "Leerdoelen",
          sublabel: "bèta",
          isMenuItem: true,
          menuSection: "main",
          roleRequired: ["webmaster", "manager", "organizer", "teamleader"],
          settingRequired: ["learningObjectives"],
        },
      },
      ...overviewPortfolioFragment,
      {
        path: "all-meetings",
        name: "overviewAllMeetings",
        component: () => import("pages/overview/OverviewAllMeetings.vue"),
        meta: {
          title: "Besprekingenoverzicht",
          isMenuItem: true,
          menuSection: "main",
          roleRequired: ["webmaster", "manager", "organizer", "teamleader"],
          settingRequired: ["studentMeeting"],
        },
      },
    ],
  },
];

export default routes;
